import { MONTHS } from '@/common/constants';
import { ActionInspectDialog } from '@/components/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AgrotechContour } from '@/features/agro-technical-events/components/table/AgrotechContour';
import { useTable } from '@/features/agro-technical-events/hooks';
import useAgroTechnicalEventsStore from '@/features/agro-technical-events/store/useAgroTechnicalEventsStore';
import { Infos } from '@/features/projects/components/single/info';
import useProjectStore from '@/features/projects/store/useProjectStore';
import { IContourAction } from '@/features/stewardship/models/IStewardship';
import { useStewardshipStore } from '@/features/stewardship/store/useStewardshipStore';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

export const StewardshipProjectDetail: FC = () => {
  const { project_id, employee_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { columns } = useTable();

  // Project info
  const { projectsById, projectsByIdLoading, fetchProjectById } =
    useProjectStore();

  // Dynamic months — stewardship store
  const { projectMonths, projectMonthsLoading, fetchProjectMonths } =
    useStewardshipStore();

  // Contours & events — agrotech store (bir xil endpointlar)
  const {
    agroTechnicalEventProjectContours,
    agroTechnicalEventProjectContoursLoading,
    agroEvents,
    agroEventsLoading,
    fetchAgroTechnicalEventProjectContours,
    fetchAgroEvents,
    actionInspectData,
    actionInspectLoading,
    fetchActionInspect,
    clearActionInspectData,
  } = useAgroTechnicalEventsStore();

  // Local state
  const [activeContour, setActiveContour] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<IContourAction | null>(
    null,
  );

  // Fetch project
  useEffect(() => {
    if (project_id) {
      fetchProjectById({ id: project_id });
    }
  }, [project_id]);

  // Fetch months
  useEffect(() => {
    if (project_id && employee_id) {
      fetchProjectMonths({ project_id, user_id: employee_id });
    }
  }, [project_id, employee_id]);

  const sortedMonths = useMemo(() => {
    if (!projectMonths.length) return [];
    const monthOrder = MONTHS.map((m) => m.value);
    return [...projectMonths].sort((a, b) => {
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    });
  }, [projectMonths]);

  const currentMonth = searchParams.get('month') || sortedMonths[0] || '';

  useEffect(() => {
    if (sortedMonths.length > 0 && !searchParams.get('month')) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('month', sortedMonths[0]);
          return newParams;
        },
        { replace: true },
      );
    }
  }, [sortedMonths]);

  const handleChangeTab = (value: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('month', value);
        return newParams;
      },
      { replace: true },
    );
    setActiveContour('');
  };

  // Fetch contours when month changes
  useEffect(() => {
    if (project_id && currentMonth) {
      fetchAgroTechnicalEventProjectContours({
        id: project_id,
        month: currentMonth,
      });
      setActiveContour('');
    }
  }, [project_id, currentMonth]);

  // Fetch events when contour is expanded
  useEffect(() => {
    if (activeContour && project_id && currentMonth) {
      const crop_id = agroTechnicalEventProjectContours.find(
        (c) => c.contour_id === activeContour,
      )?.crop_id;
      fetchAgroEvents({
        month: currentMonth,
        contour_id: activeContour,
        project_id,
        crop_id,
      });
    }
  }, [activeContour, project_id, currentMonth]);

  // Dialog
  const handleOpen = (action: IContourAction) => {
    setSelectedAction(action);
    setOpenModal(true);
  };

  useEffect(() => {
    if (openModal && selectedAction) {
      fetchActionInspect({
        farmer_action_by_project_id:
          selectedAction.farmer_action_by_project_id,
        farmer_action_by_project_contour_id:
          selectedAction.farmer_action_by_project_contour_id,
        contour_id: activeContour,
      });
    } else if (!openModal) {
      setSelectedAction(null);
    }
  }, [openModal, selectedAction]);

  return (
    <div className="flex flex-col gap-6">
      {projectsByIdLoading ? (
        <Skeleton className="h-[28px] w-32" />
      ) : (
        <h1 className="text-lg font-semibold text-dark-blue">
          {projectsById?.regnum}
        </h1>
      )}
      <Infos items={projectsById} isLoading={projectsByIdLoading} />

      {projectMonthsLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : (
        sortedMonths.length > 0 && (
          <Tabs
            value={currentMonth}
            onValueChange={handleChangeTab}
            className="w-full"
          >
            <TabsList
              className="grid w-[1000px]"
              style={{
                gridTemplateColumns: `repeat(${sortedMonths.length}, minmax(0, 1fr))`,
              }}
            >
              {sortedMonths.map((month) => {
                const monthConfig = MONTHS.find((m) => m.value === month);
                return monthConfig ? (
                  <TabsTrigger value={month} key={month}>
                    {t(month)}
                  </TabsTrigger>
                ) : null;
              })}
            </TabsList>
            {sortedMonths.map((month) => (
              <TabsContent value={month} key={month}>
                <div className="mt-4 flex flex-col gap-5">
                  <h4 className="text-lg font-semibold">
                    {t('all.contours')}
                  </h4>
                  {agroTechnicalEventProjectContoursLoading ? (
                    <Skeleton className="h-[200px] w-full rounded-xl" />
                  ) : (
                    <AgrotechContour
                      contours={agroTechnicalEventProjectContours}
                      activeContour={activeContour}
                      onContourChange={setActiveContour}
                      agroEvents={agroEvents}
                      eventsLoading={agroEventsLoading}
                      columns={columns}
                      pageSize={10}
                      currentPage={1}
                      onRowClick={handleOpen}
                    />
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )
      )}

      <ActionInspectDialog
        open={openModal}
        onOpenChange={setOpenModal}
        selectedAction={selectedAction}
        actionInspectData={actionInspectData}
        actionInspectLoading={actionInspectLoading}
        onClear={clearActionInspectData}
      />
    </div>
  );
};
